import {
    CodeBlock, BlockType, SettingType, Define, SettingState, ValidationMessage,
    TibboDataType,
} from '@appblocks/codegeneration/types';

import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';
import * as micropython from './micropython';
import * as zephyr from './zephyr';
import * as tios from './tios';
import * as simulator from './simulator';


export default class GlobalVariables extends BaseFeature {
    constructor() {
        super();
        this.name = 'Variables';
        this.id = 'global_vars';
        this.icon = 'HiOutlineVariable';
        const settings = [
            {
                id: 'INTERVAL',
                name: 'Update Interval (seconds)',
                settingType: SettingType.NUMBER,
                min: 1,
                max: 600,
                required: true,
                default: '1',
                helpText: 'Determines how often variables are updated. The minimum interval is 1 second; the maximum is 600 seconds (10 minutes).',
            },
            {
                id: 'DEBUG_PRINT',
                name: 'Debug Printing',
                settingType: SettingType.OPTION,
                default: '0',
                options: [
                    {
                        name: 'Disabled',
                        value: '0',
                    },
                    {
                        name: 'Enabled',
                        value: '1',
                    },
                ],
                helpText: 'When this option is enabled, variables\' values are printed to the logging display (selected in the General features page) when they are updated.',
            },
            {
                id: 'VARS',
                name: 'Variables',
                settingType: SettingType.ARRAY,
                required: true,
                refId: 'variable',
                default: new Array<BaseFeature>(0),
                helpText: 'Variables are used in blocks to contain and manipulate user-defined values.',
            },
            {
                id: 'AUTOVARS',
                name: 'Auto-Generated Variables',
                settingType: SettingType.ARRAY,
                required: true,
                readonly: true,
                refId: 'variable',
                helpText: 'Automatically generated variables are linked to hardware or sensor values.',
                default: new Array<BaseFeature>(0),
            },
        ];

        this.settings = this.settings.concat(settings);
    }

    getEnabled(): boolean {
        const vars = this.getSettingValue('VARS').concat(this.getSettingValue('AUTOVARS'));
        return vars.length > 0;
    }

    getDependencies(): string[] {
        const vars = this.getSettingValue('VARS').concat(this.getSettingValue('AUTOVARS'));
        for (let i = 0; i < vars.length; i += 1) {
            if (vars[i].getSettingValue('DATATYPE') === TibboDataType.DATETIME
                || vars[i].getSettingValue('DATATYPE') === TibboDataType.TIME) {
                return ['time'];
            }
        }
        return [];
    }

    getDefines(): Array<Define> {
        const DEBUG_PRINT = this.getSettingValue('DEBUG_PRINT') === '1';
        if (DEBUG_PRINT) {
            return [{
                name: 'VARIABLES_DEBUG_PRINT',
                value: '1',
            }];
        }
        return [];
    }

    getCodeBlocks(): CodeBlock[] {
        switch (this.project.runtime) {
            case 'micropython':
                return micropython.getCodeBlocks(this);
            case 'zephyr':
                return zephyr.getCodeBlocks(this);
            case 'simulator':
                return simulator.getCodeBlocks(this);
            default:
                return tios.getCodeBlocks(this);
        }
    }


    getDetails(): string {
        return `
# Variables
Variables can be one of two things:
- A link to a hardware or sensor value
- A container of a value

Variables can be used in the **Variable Set/Math** and **On Variable Changed** blocks:

![variable blocks](/static/img/blocks_variables.png)

## Hardware and Sensor Values
When hardware is added to the project, variables are automatically generated for each sensor or GPIO line.

In the case of sensors, the variable is linked to the sensor's value. This means that the variable's value is updated at a regular interval, 
and is the same as the sensor's value.

In the case of GPIO lines, the variable is linked to the GPIO line's state. Setting the variable's value to 1 will set the GPIO line 
to HIGH, and setting the variable's value to 0 will set the GPIO line to LOW.

## Variables as Containers of Value
Variables can be used to store a value. This value can be a number, a string, a datetime, or a time.

Variable Values can be used directly in block parameters like this:

![variable blocks](/static/img/variables_token.png)

## Variable Types
Variables can be of the following types:
- Number (Integer or Decimal)
- String (Text)
- DateTime (Date and Time)
- Time (Time of day)

### Number
A number can be an integer or a decimal. A number is stored in IEEE754 format.

### String
A string is a sequence of characters. Strings are stored in UTF-8 format.

### DateTime
A datetime is a date and time. A datetime is stored in the Unix Timestamp format.
This is the number of seconds since 1970-01-01 00:00:00 UTC.

### Time
A time is a time of day. A time is stored in the format of number of seconds since midnight.

`;
    }

    getDocURL(): string {
        return `${process.env.HOME_URL}/docs/features/variables`;
    }
}
